import type { FixtureElement } from "./types";

export const LOCAL_VALIDATION_OUTCOMES = ["pass", "missing", "not-applicable", "refused"] as const;
export type LocalValidationOutcome = (typeof LOCAL_VALIDATION_OUTCOMES)[number];

export interface LocalValidationRule {
  readonly id: string;
  readonly version: "workbench-candidate-rules/1";
  readonly label: string;
  readonly target:
    | { readonly kind: "property"; readonly property: string }
    | { readonly kind: "classification" }
    | { readonly kind: "component-reference" };
  readonly appliesTo: readonly string[] | null;
  readonly status: "candidate";
}

export interface LocalValidationEntry {
  readonly elementId: string;
  readonly outcome: LocalValidationOutcome;
  readonly reason: string;
}

export interface LocalValidationResult {
  readonly state: "partial";
  readonly rule: LocalValidationRule;
  readonly assessedRecords: number;
  readonly counts: Readonly<Record<LocalValidationOutcome, number>>;
  readonly entries: readonly LocalValidationEntry[];
}

export interface LocalValidationRuleSummary {
  readonly rule: LocalValidationRule;
  readonly counts: Readonly<Record<LocalValidationOutcome, number>>;
}

export interface LocalValidationOverview {
  readonly state: "partial";
  readonly recordCount: number;
  readonly assessmentCount: number;
  readonly counts: Readonly<Record<LocalValidationOutcome, number>>;
  readonly rules: readonly LocalValidationRuleSummary[];
}

export interface LocalValidationTypeSummary {
  readonly type: string;
  readonly records: number;
  readonly renderable: number;
  readonly editable: number;
  readonly unmapped: number;
  readonly unsupported: number;
  readonly assessmentCount: number;
  readonly counts: Readonly<Record<LocalValidationOutcome, number>>;
  readonly elementIds: readonly string[];
}

export interface LocalValidationOutcomePlan {
  readonly ruleId: string;
  readonly outcome: LocalValidationOutcome;
  readonly matchedElementIds: readonly string[];
  readonly editableElementIds: readonly string[];
  readonly refusedElementIds: readonly string[];
}

export const localValidationRules: readonly LocalValidationRule[] = [
  {
    id: "wall-fire-rating",
    version: "workbench-candidate-rules/1",
    label: "Wall FireRating present",
    target: { kind: "property", property: "FireRating" },
    appliesTo: ["Wall"],
    status: "candidate"
  },
  {
    id: "classification-reference",
    version: "workbench-candidate-rules/1",
    label: "Classification mapping present",
    target: { kind: "classification" },
    appliesTo: null,
    status: "candidate"
  },
  {
    id: "wall-component-reference",
    version: "workbench-candidate-rules/1",
    label: "Wall component reference present",
    target: { kind: "component-reference" },
    appliesTo: ["Wall"],
    status: "candidate"
  }
];

function hasValue(value: unknown): boolean {
  return value !== null && value !== undefined && (typeof value !== "string" || value.trim() !== "");
}

function emptyCounts(): Record<LocalValidationOutcome, number> {
  return Object.fromEntries(LOCAL_VALIDATION_OUTCOMES.map((outcome) => [outcome, 0])) as Record<LocalValidationOutcome, number>;
}

function outcomeFor(element: FixtureElement, rule: LocalValidationRule): LocalValidationOutcome {
  if (rule.appliesTo !== null && !rule.appliesTo.includes(element.type)) {
    return "not-applicable";
  }
  if (element.type === "Skipped" || (element.properties.ValueKind !== undefined && element.properties.ValueKind !== "object")) {
    return "refused";
  }
  if (rule.target.kind === "classification") {
    return element.classification !== null && hasValue(element.classification.code) ? "pass" : "missing";
  }
  const property = rule.target.kind === "component-reference" ? "ComponentReference" : rule.target.property;
  return hasValue(element.properties[property]) ? "pass" : "missing";
}

function entryFor(element: FixtureElement, rule: LocalValidationRule): LocalValidationEntry {
  const outcome = outcomeFor(element, rule);
  if (outcome === "not-applicable") {
    return { elementId: element.id, outcome, reason: `Rule scope does not include ${element.type}.` };
  }
  if (outcome === "refused") {
    return { elementId: element.id, outcome, reason: "The retained evidence is not a reconstructed object record." };
  }
  if (rule.target.kind === "classification") {
    return {
      elementId: element.id,
      outcome,
      reason: outcome === "pass" ? "A classification mapping is present." : "No classification mapping is present."
    };
  }
  const property = rule.target.kind === "component-reference" ? "ComponentReference" : rule.target.property;
  return {
    elementId: element.id,
    outcome,
    reason: outcome === "pass" ? `${property} is present.` : `${property} is missing or blank.`
  };
}

export function evaluateLocalValidation(
  elements: readonly FixtureElement[],
  rule: LocalValidationRule
): LocalValidationResult {
  const entries = [...elements]
    .sort((left, right) => left.id.localeCompare(right.id))
    .map((element) => entryFor(element, rule));
  const counts = emptyCounts();
  for (const entry of entries) counts[entry.outcome] += 1;
  return { state: "partial", rule, assessedRecords: entries.length, counts, entries };
}

export function summarizeLocalValidation(
  elements: readonly FixtureElement[],
  rules: readonly LocalValidationRule[] = localValidationRules
): LocalValidationOverview {
  const orderedRules = [...rules].sort((left, right) => left.id.localeCompare(right.id));
  if (new Set(orderedRules.map((rule) => rule.id)).size !== orderedRules.length) {
    throw new RangeError("Candidate validation overview requires unique rule ids.");
  }
  const counts = emptyCounts();
  const summaries = orderedRules.map((rule) => {
    const ruleCounts = emptyCounts();
    for (const element of elements) {
      const outcome = outcomeFor(element, rule);
      ruleCounts[outcome] += 1;
      counts[outcome] += 1;
    }
    return { rule, counts: ruleCounts };
  });
  return {
    state: "partial",
    recordCount: elements.length,
    assessmentCount: elements.length * orderedRules.length,
    counts,
    rules: summaries
  };
}

export function summarizeLocalValidationByType(
  elements: readonly FixtureElement[],
  rules: readonly LocalValidationRule[] = localValidationRules
): readonly LocalValidationTypeSummary[] {
  const uniqueRuleIds = new Set(rules.map((rule) => rule.id));
  if (uniqueRuleIds.size !== rules.length) throw new RangeError("Candidate type summary requires unique rule ids.");
  const groups = new Map<string, FixtureElement[]>();
  for (const element of elements) {
    const group = groups.get(element.type) ?? [];
    group.push(element);
    groups.set(element.type, group);
  }
  return [...groups.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([type, typeElements]) => {
      const counts = emptyCounts();
      for (const element of typeElements) {
        for (const rule of rules) counts[outcomeFor(element, rule)] += 1;
      }
      return {
        type,
        records: typeElements.length,
        renderable: typeElements.filter((element) => element.geometry !== null).length,
        editable: typeElements.filter((element) => element.editable && element.unsupportedReason === undefined).length,
        unmapped: typeElements.filter((element) => element.classification === null).length,
        unsupported: typeElements.filter((element) => element.unsupportedReason !== undefined).length,
        assessmentCount: typeElements.length * rules.length,
        counts,
        elementIds: typeElements.map((element) => element.id).sort((left, right) => left.localeCompare(right))
      };
    });
}

export function planLocalValidationOutcome(
  elements: readonly FixtureElement[],
  result: LocalValidationResult,
  outcome: LocalValidationOutcome
): LocalValidationOutcomePlan {
  const elementsById = new Map(elements.map((element) => [element.id, element]));
  const matchedElementIds = result.entries
    .filter((entry) => entry.outcome === outcome)
    .map((entry) => entry.elementId);
  if (new Set(matchedElementIds).size !== matchedElementIds.length) {
    throw new RangeError("Candidate validation outcome contains duplicate element ids.");
  }
  const unknown = matchedElementIds.filter((id) => !elementsById.has(id));
  if (unknown.length > 0) {
    throw new RangeError(`Candidate validation outcome is stale for this inspection dataset: ${unknown.join(", ")}`);
  }
  const editableElementIds: string[] = [];
  const refusedElementIds: string[] = [];
  for (const id of matchedElementIds) {
    const element = elementsById.get(id)!;
    if (element.editable && element.unsupportedReason === undefined) editableElementIds.push(id);
    else refusedElementIds.push(id);
  }
  return { ruleId: result.rule.id, outcome, matchedElementIds, editableElementIds, refusedElementIds };
}
